import React from 'react';

import { type CloseOverlayHandler, DescriptionListDefault, Dropdown, HintPopup, type PlacementType } from '..';
import { ListItemIcon } from './ListItemIcon';
import { Menu } from './Menu';
import { MenuItem } from './MenuItem';
import { Divider } from '@cobliteam/ui/components';
import ListItemText from '@material-ui/core/ListItemText';

export interface DropdownOption<T> {
  key: T;
  value: React.ReactNode;
  icon?: JSX.Element;
  disabled?: boolean;
  hintText?: string;
  divider?: boolean;
}

interface DropdownMenuProps<T> {
  children: React.ReactNode;
  onClick?: (option: DropdownOption<T>) => void;
  options: Array<DropdownOption<T>>;
  placement?: PlacementType;
  divider?: boolean;
  lastOptionDivider?: boolean;
  disableMarginBetweenOptions?: boolean;
  className?: string;
  disabled?: boolean;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export const DropdownMenu = <T extends string>({
  children,
  options,
  onClick,
  placement,
  divider = true,
  lastOptionDivider = false,
  disableMarginBetweenOptions = false,
  className = '',
  disabled = false,
}: DropdownMenuProps<T>) => {
  const overlay = (closeOverlayHandler: CloseOverlayHandler) => {
    const clickHandler = (option: DropdownOption<T>) => {
      closeOverlayHandler();

      if (onClick) {
        onClick(option);
      }
    };

    return (
      <Menu>
        <ul className={className}>
          {options.map((option, index) => (
            <React.Fragment key={option.key}>
              <HintPopup enabled={Boolean(option.hintText)} title={option.hintText || ''} arrow placement='bottom'>
                <div>
                  <MenuItem
                    disabled={option.disabled}
                    onClick={() => clickHandler(option)}
                    hasIconOnOptions={options.some((item) => item.icon)}
                    disableMarginBetweenOptions={disableMarginBetweenOptions}
                  >
                    <>
                      {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                      <ListItemText disableTypography>
                        <DescriptionListDefault>{option.value}</DescriptionListDefault>
                      </ListItemText>
                    </>
                  </MenuItem>
                </div>
              </HintPopup>
              {index < options.length - 1 && (option.divider || divider) && <Divider />}
              {index === options.length - 2 && lastOptionDivider && <Divider />}
            </React.Fragment>
          ))}
        </ul>
      </Menu>
    );
  };

  return (
    <Dropdown overlay={overlay} placement={placement} disabled={disabled}>
      {children}
    </Dropdown>
  );
};
